// My Campaigns page component for InfluenceHub MVP
// Shows all campaigns created by the current brand with their status
// Allows brands to track campaign progress and status

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebaseConfig.js';
import Navbar from '../../components/layout/Navbar';
import Sidebar from '../../components/layout/Sidebar';

const MyCampaigns = ({ onNavigate }) => {
  const { user, profile } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch campaigns for current brand
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        console.log('🔄 Fetching campaigns for brand:', user?.uid);
        
        if (!user || !profile) {
          console.log('❌ No user or profile found');
          setLoading(false);
          return;
        }

        if (!db) {
          throw new Error('Firebase database not initialized');
        }

        // Simplified query without orderBy to avoid index issues
        const campaignsQuery = query(
          collection(db, 'campaigns'),
          where('brandId', '==', user.uid)
        );
        
        const campaignsSnapshot = await getDocs(campaignsQuery);
        const campaignsData = campaignsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          deadline: doc.data().deadline || null, // Can be string or Timestamp
          updatedAt: doc.data().updatedAt?.toDate() || new Date()
        }));
        
        // Sort by creation date manually
        campaignsData.sort((a, b) => b.createdAt - a.createdAt);
        
        console.log('📊 Found campaigns:', campaignsData.length);
        setCampaigns(campaignsData);
      } catch (error) {
        console.error('❌ Error fetching campaigns:', error);
        setError(`Failed to load campaigns: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [user, profile]);

  // Format date
  const formatDate = (date) => {
    if (!date) return 'Not specified';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return 'Not specified';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_approval':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">⏳ Pending Approval</span>;
      case 'approved':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">✅ Approved</span>;
      case 'rejected':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">❌ Rejected</span>;
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">🚀 Active</span>;
      case 'completed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">✅ Completed</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Unknown</span>;
    }
  };

  // Get status count
  const getStatusCount = (status) => {
    return campaigns.filter(campaign => campaign.status === status).length;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar onNavigate={onNavigate} />
        <div className="flex">
          <Sidebar onNavigate={onNavigate} />
          <main className="flex-1 p-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading campaigns...</p>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar onNavigate={onNavigate} />
        <div className="flex">
          <Sidebar onNavigate={onNavigate} />
          <main className="flex-1 p-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="text-red-600 text-6xl mb-4">⚠️</div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Campaigns</h1>
                  <p className="text-gray-600 mb-6">{error}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation bar */}
      <Navbar onNavigate={onNavigate} />
      
      <div className="flex">
        {/* Sidebar navigation */}
        <Sidebar onNavigate={onNavigate} />
        
        {/* Main content area */}
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page header */}
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-gray-900">My Campaigns</h1>
              <p className="mt-2 text-gray-600">
                Track and manage all your influencer marketing campaigns
              </p>
            </div>

            {/* Campaign Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Campaigns</h3>
                <p className="text-3xl font-bold text-blue-600">{campaigns.length}</p>
                <p className="text-sm text-gray-500">All time</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Pending Approval</h3>
                <p className="text-3xl font-bold text-yellow-600">{getStatusCount('pending_approval')}</p>
                <p className="text-sm text-gray-500">Awaiting review</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Active</h3>
                <p className="text-3xl font-bold text-green-600">{getStatusCount('approved') + getStatusCount('active')}</p>
                <p className="text-sm text-gray-500">Running campaigns</p>
              </div>
            </div>

            {/* Campaigns List */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">All Campaigns</h2>
                  <button
                    onClick={() => onNavigate('create-campaign')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    + Create New Campaign
                  </button>
                </div>
              </div>
              
              {campaigns.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="text-gray-400 text-6xl mb-4">📋</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns yet</h3>
                  <p className="text-gray-600 mb-6">Create your first campaign to get started with influencer marketing.</p>
                  <button
                    onClick={() => onNavigate('create-campaign')}
                    className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Create Your First Campaign
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-6 font-medium text-gray-600">Campaign</th>
                        <th className="text-left p-6 font-medium text-gray-600">Status</th>
                        <th className="text-left p-6 font-medium text-gray-600">Budget</th>
                        <th className="text-left p-6 font-medium text-gray-600">Content Submission Deadline</th>
                        <th className="text-left p-6 font-medium text-gray-600">Goal</th>
                        <th className="text-left p-6 font-medium text-gray-600">Created</th>
                        <th className="text-left p-6 font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map((campaign) => (
                        <tr 
                          key={campaign.id} 
                          className="border-b hover:bg-gray-50 cursor-pointer relative"
                          onClick={() => onNavigate('brand-campaign-detail', campaign.id)}
                        >
                          <td className="p-6">
                            <div>
                              <p className="font-medium text-gray-900">{campaign.campaignName || campaign.title || campaign.name}</p>
                              <p className="text-sm text-gray-500 truncate max-w-xs">{campaign.brief || campaign.bio || campaign.description}</p>
                            </div>
                          </td>
                          <td className="p-6">
                            {getStatusBadge(campaign.status)}
                          </td>
                          <td className="p-6">
                            <span className="font-medium text-green-600">{formatCurrency(campaign.budget || campaign.budgetUSD)}</span>
                          </td>
                          <td className="p-6 text-sm text-gray-500">
                            {typeof campaign.deadline === 'string' 
                              ? campaign.deadline 
                              : formatDate(campaign.deadline)}
                          </td>
                          <td className="p-6">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                              {campaign.goal || campaign.collaborationType || '—'}
                            </span>
                          </td>
                          <td className="p-6 text-sm text-gray-500">
                            {formatDate(campaign.createdAt)}
                          </td>
                          <td className="p-6">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onNavigate('brand-campaign-detail', campaign.id);
                              }}
                              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors z-10 relative"
                              aria-label={`View ${campaign.campaignName || campaign.title || campaign.name}`}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MyCampaigns;
